import { useCallback, useEffect, useRef } from 'react';

import posthog from 'posthog-js';
import { useNavigate } from 'react-router';

import detectPossiblePartyEmailAddresses from '@/features/analysis/utils/detectPossiblePartyEmailAddresses';
import useEmailClient from '@/features/email/hooks/useEmailClient';
import useMessagesStore from '@/features/email/stores/useMessagesStore';
import useAuth from '@/hooks/useAuth';
import paths from '@/lib/paths';
import useLocalSettings from '@/stores/useLocalSettings';

import { useModal } from '../../ModalProvider/ModalProvider';

import useMailboxEventHandlers from './useMailboxEventHandlers';

export default function useEmailData() {
  const navigate = useNavigate();

  const { getSelectedMessageDetails, getSelectedMessageId } = useEmailClient();
  const actions = useMessagesStore((state) => state.actions);
  const messages = useMessagesStore((state) => state.messages);

  const { broker } = useAuth();

  const { toggleOnlyShowRelevant, setIsLikelyPinned } = useLocalSettings(
    (state) => state.actions,
  );

  const { closeFullscreenModals } = useModal();

  const lastProcessedMessageId = useRef<string | null>(null);

  const selectedMessageId = getSelectedMessageId();
  const selectedMessage = selectedMessageId
    ? messages?.[selectedMessageId]
    : undefined;

  const updateEmailData = useCallback(async () => {
    const messageId = getSelectedMessageId();
    if (!messageId) return;

    if (lastProcessedMessageId.current === messageId) return;
    lastProcessedMessageId.current = messageId;

    const message = await getSelectedMessageDetails();
    if (!message) return;

    const [fromCandidates, allAddresses] = detectPossiblePartyEmailAddresses(
      message.fromAddress,
      message.threadEmailAddresses,
      broker?.email,
    );

    const newMessageData = {
      messageId: message.messageId,
      subject: message.subject,
      fromCandidates,
      allAddresses,
    };

    posthog.capture('Update selected email data', {
      emailData: newMessageData,
      matchData: {
        fromCandidates,
        fromAddress: message.fromAddress,
        threadEmailAddresses: message.threadEmailAddresses,
        ownEmail: broker?.email,
      },
    });

    actions.updateMessage(message.messageId, newMessageData);
  }, [actions, broker?.email, getSelectedMessageDetails, getSelectedMessageId]);

  useMailboxEventHandlers(() => {
    toggleOnlyShowRelevant(true);
    navigate(paths.assistant);
    closeFullscreenModals();
    setIsLikelyPinned(true);

    updateEmailData();
  });

  useEffect(() => {
    if (selectedMessage) return;

    updateEmailData();
  }, [selectedMessage, updateEmailData]);

  return selectedMessage;
}
